import React, { useState, useEffect, useRef, useCallback } from "react";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  Link as RemixLink,
  useActionData,
  useNavigation,
  useSearchParams,
} from "@remix-run/react";
import { User } from "#app/models/index.js";
import * as Passkey from "#app/models/Passkey.js";
import { sendEmail, InactiveRecipientsError } from "#app/utils/email.server.js";
import { validatePublicPost } from "#app/utils/security.server.js";
import { badRequest } from "#app/utils/response.server.js";
import { generateToken } from "#app/utils/jwt.server.js";
import { getUserId } from "#app/utils/session.server.js";
import { safeRedirect } from "#app/utils/redirect.js";
import { ArrowBackIcon as BackIcon, EmailIcon } from "@chakra-ui/icons";
import {
  Heading,
  Text,
  Stack,
  Input,
  FormControl,
  FormLabel,
  Link,
  Button,
  Container,
  HStack,
  VisuallyHidden,
  Divider,
  Icon,
  Alert,
  AlertTitle,
  AlertDescription,
  Box,
} from "@chakra-ui/react";

import { CallToAction, Paper } from "#app/components/index.jsx";

import { EMAIL_REGEX } from "#app/utils/regex.js";
import { GoPasskeyFill } from "react-icons/go";

export function meta() {
  return [
    { title: "Login | Schnerp" },
    {
      name: "description",
      content:
        "Log into your Schnerp account to check your requested campground availability",
    },
  ];
}

export async function loader({ request }) {
  const userId = await getUserId(request);
  if (userId) {
    const url = new URL(request.url);
    const redirectTo = safeRedirect(
      url.searchParams.get("redirectTo"),
      "/browse",
    );
    return redirect(redirectTo);
  }
  return null;
}

export async function action({ request }) {
  const form = await validatePublicPost(request);
  const { email } = form;

  if (!email || !email.match(EMAIL_REGEX)) {
    return badRequest({
      error: "Invalid email address",
      field: "email",
    });
  }

  // logged out db user
  const user = await User.findByEmail(email);

  // new user
  // return json so we can handle this error on the form
  if (!user) {
    return badRequest({
      error:
        "No account found. We'll make an account after you create your first request.",
      field: "email",
    });
  }

  const token = await generateToken({ id: user.id });

  if (
    process.env.APP_ENV === "development" &&
    process.env.DEV_AUTO_LOGIN === "true"
  ) {
    return redirect(`/login/${token}`);
  }

  try {
    await sendEmail({
      template: "login",
      to: user.email,
      locals: {
        token,
      },
    });
  } catch (e) {
    if (e instanceof InactiveRecipientsError) {
      return badRequest({
        error:
          "This account has been disabled due to a previous bounce or spam complaint. Please email help@schnerp.com with a screenshot of this message to re-activate your account.",
        field: "email",
      });
    }
    throw e;
  }

  const hasPasskeys = await Passkey.hasPasskeys(user.id);

  return json({
    email: user.email,
    success: true,
    hasPasskeys,
  });
}

export default function Login() {
  const data = useActionData();
  const { state } = useNavigation();
  const [searchParams] = useSearchParams();
  const isSubmitting = state === "submitting";
  const inputRef = useRef();

  // Passkey state - default to true to avoid layout shift on modern browsers
  const [passkeyError, setPasskeyError] = useState(null);
  const [isAuthenticatingPasskey, setIsAuthenticatingPasskey] = useState(false);
  const [supportsPasskeys, setSupportsPasskeys] = useState(true);

  // Signal to passkey provider that a credential doesn't exist on our server
  const signalUnknownCredential = useCallback(async (credentialId) => {
    try {
      if (
        window.PublicKeyCredential?.signalUnknownCredential &&
        typeof credentialId === "string"
      ) {
        await window.PublicKeyCredential.signalUnknownCredential({
          rpId: window.location.hostname,
          credentialId,
        });
      }
    } catch (e) {
      // Signal API is best-effort, ignore errors
      console.debug("signalUnknownCredential failed:", e);
    }
  }, []);

  // Handle successful passkey auth - check for cross-device and redirect
  const handleAuthSuccess = useCallback(async (result, credential) => {
    // Check if this was a cross-device authentication
    // If so, we might want to prompt creating a local passkey later
    if (credential?.authenticatorAttachment === "cross-platform") {
      // Store flag in sessionStorage - the account page can check this
      // and prompt the user to create a local passkey
      sessionStorage.setItem("passkeyAuthCrossDevice", "true");
    }

    if (result.redirect) {
      window.location.href = result.redirect;
    }
  }, []);

  // Verify passkey with server
  const verifyPasskey = useCallback(
    async (credential, email) => {
      const redirectTo = safeRedirect(
        searchParams.get("redirectTo"),
        "/browse",
      );
      const verifyResponse = await fetch("/api/passkey/auth-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credential:
            typeof credential === "string"
              ? credential
              : JSON.stringify(credential),
          email,
          redirectTo,
        }),
      });

      const result = await verifyResponse.json();

      // Handle unknown credential - signal to passkey provider
      if (result.error === "unknownCredential" && result.credentialId) {
        await signalUnknownCredential(result.credentialId);
        throw new Error(
          "This passkey is no longer registered. Please remove it from your password manager and try again.",
        );
      }

      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    },
    [searchParams, signalUnknownCredential],
  );

  // Check passkey support on mount
  useEffect(() => {
    async function checkPasskeySupport() {
      try {
        const { browserSupportsWebAuthn } = await import(
          "@simplewebauthn/browser"
        );
        if (!browserSupportsWebAuthn()) {
          setSupportsPasskeys(false);
        }
      } catch {
        setSupportsPasskeys(false);
      }
    }
    checkPasskeySupport();
  }, []);

  useEffect(() => {
    if (data?.error) {
      inputRef.current?.focus();
    }
  }, [data]);

  // Clear passkey error when form is submitted (so only one error shows at a time)
  useEffect(() => {
    if (isSubmitting) {
      setPasskeyError(null);
    }
  }, [isSubmitting]);

  // Manual passkey authentication handler (button click)
  const handlePasskeyAuth = async () => {
    setPasskeyError(null);
    setIsAuthenticatingPasskey(true);

    try {
      // 1. Get authentication options from server (no email needed - discoverable credentials)
      const optionsResponse = await fetch("/api/passkey/auth-options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conditional: true }),
      });

      if (!optionsResponse.ok) {
        const error = await optionsResponse.json();
        throw new Error(error.error || "Failed to get authentication options");
      }

      const options = await optionsResponse.json();

      // 2. Start authentication (triggers browser prompt with all passkeys for this site)
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const credential = await startAuthentication({ optionsJSON: options });

      // 3. Verify with server (user identified from credential)
      const result = await verifyPasskey(credential);

      // 4. Handle success (check cross-device, redirect)
      await handleAuthSuccess(result, credential);
    } catch (error) {
      console.error("Passkey auth error:", error);
      if (error.name === "NotAllowedError") {
        setPasskeyError(
          "No passkey found? Use the magic link below to sign in.",
        );
      } else {
        setPasskeyError(error.message || "Passkey authentication failed");
      }
    } finally {
      setIsAuthenticatingPasskey(false);
    }
  };

  // After successful magic link send
  if (data?.success) {
    return (
      <>
        <Container maxW="md" py={{ base: "12", md: "24" }}>
          <Stack spacing="6">
            <Stack spacing={{ base: "2", md: "3" }} textAlign="center">
              <Heading size="sm" fontWeight="extrabold">
                Check your inbox
              </Heading>
            </Stack>
            <Paper p={4} data-testid="login-success">
              <Stack spacing={3}>
                <Text>
                  We sent a login link to <strong>{data.email}</strong>
                </Text>
                <Text color="muted" fontSize="sm">
                  The link expires in 15 minutes. Check your spam folder if you
                  don't see it.
                </Text>
                <Button
                  colorScheme="brand"
                  variant="link"
                  leftIcon={<Icon as={BackIcon} />}
                  as={RemixLink}
                  to="."
                  size="sm"
                >
                  Use a different email
                </Button>
              </Stack>
            </Paper>

            {/* Educational CTA - plant the seed for passkey setup */}
            {supportsPasskeys && !data.hasPasskeys && (
              <Alert status="info">
                <Box mr={3}>
                  <Icon as={GoPasskeyFill} boxSize={5} />
                </Box>
                <Box>
                  <AlertTitle fontSize="sm">
                    Sign in faster next time
                  </AlertTitle>
                  <AlertDescription fontSize="sm" color="muted">
                    After logging in, create a passkey to use your fingerprint,
                    face, or screen lock to verify it's you.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </Stack>
        </Container>
        <CallToAction
          heading="No account yet?"
          body="We'll make a free account after you create your first request."
          buttonText="Create a request"
          buttonProps={{ as: RemixLink, to: "/new" }}
        />
      </>
    );
  }

  return (
    <>
      <Container maxW="md" py={{ base: "12", md: "24" }}>
        <Stack spacing="8">
          <Stack spacing="6">
            <Stack spacing={{ base: "2", md: "3" }} textAlign="center">
              <Heading size="sm" fontWeight="extrabold">
                Log in to your account
              </Heading>
            </Stack>
          </Stack>

          <Stack spacing="4">
            {/* Primary action: Passkey (if supported) */}
            {supportsPasskeys && (
              <Button
                onClick={handlePasskeyAuth}
                isLoading={isAuthenticatingPasskey}
                loadingText="Signing in..."
                variant="primary"
                size="lg"
                leftIcon={<Icon as={GoPasskeyFill} boxSize={5} />}
              >
                Sign in with passkey
              </Button>
            )}

            {/* Passkey error */}
            {passkeyError && (
              <Text color="error" fontSize="sm">
                {passkeyError}
              </Text>
            )}

            {/* Divider (only when passkeys supported) */}
            {supportsPasskeys && (
              <HStack>
                <Divider />
                <Text fontSize="sm" color="subtle" whiteSpace="nowrap">
                  or
                </Text>
                <Divider />
              </HStack>
            )}

            {/* Magic link form */}
            <Form method="post" id="magic-link-form">
              <input
                type="hidden"
                name="redirectTo"
                value={safeRedirect(searchParams.get("redirectTo"), "")}
              />
              <Stack spacing="3">
                <FormControl isRequired>
                  <VisuallyHidden>
                    <FormLabel>Email</FormLabel>
                  </VisuallyHidden>
                  <Input
                    ref={inputRef}
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="email"
                  />
                </FormControl>
                {/* Email error (only if no passkey error) */}
                {data?.field === "email" && !passkeyError && (
                  <Text color="error" fontSize="sm">
                    {data.error}
                  </Text>
                )}
                <Button
                  type="submit"
                  variant={supportsPasskeys ? "outline" : "primary"}
                  size="lg"
                  width="full"
                  isLoading={isSubmitting}
                  loadingText="Sending..."
                  leftIcon={<EmailIcon />}
                >
                  Email me a login link
                </Button>
              </Stack>
            </Form>
          </Stack>

          {/* Help link */}
          <HStack justify="center" spacing="1">
            <Text fontSize="sm" color="subtle">
              Having issues? Email us at
            </Text>
            <Button
              as={Link}
              isExternal
              href="mailto:help@schnerp.com?subject=Login%20Help"
              variant="link"
              colorScheme="brand"
              size="sm"
            >
              help@schnerp.com
            </Button>
          </HStack>
        </Stack>
      </Container>

      <CallToAction
        heading="No account yet?"
        body="We'll make a free account after you create your first request."
        buttonText="Create a request"
        buttonProps={{ as: RemixLink, to: "/new" }}
      />
    </>
  );
}
